import { useCallback, useEffect, useReducer } from 'react';
import { fetchBillingHistory } from '~/api';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';

export interface IBillingItem {
  id: number;
  amount: string;
  billing_date: string;
  expiry_date: string | null;
  plan_name: string;
  status: 'paid' | 'pending' | 'failed' | 'succeeded';
  subscription_id: string;
  payment_id: string;
}

interface IBillingHistoryApiResponse {
  next: string | null;
  previous: string | null;
  count: number;
  total_pages: number;
  results: IBillingItem[];
}

interface State {
  listIds: string[];
  listData: Record<string, IBillingItem>;
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
}

const initialState: State = {
  listIds: [],
  listData: {},
  isLoading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
};

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: { billing: IBillingHistoryApiResponse; page: number } }
  | { type: 'FETCH_FAILURE'; error: string }
  | { type: 'RESET_LIST' };

const billingReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_SUCCESS':
      const { billing, page } = action.payload;
      const newIds = billing.results.map(item => item.id.toString());
      const newData = billing.results.reduce<Record<string, IBillingItem>>((acc, item) => {
        acc[item.id.toString()] = item;
        return acc;
      }, {});

      return {
        ...state,
        isLoading: false,
        listIds: newIds,
        listData: newData,
        currentPage: page,
        totalPages: billing.total_pages,
        totalCount: billing.count,
      };

    case 'FETCH_FAILURE':
      return { ...state, isLoading: false, error: action.error };

    case 'RESET_LIST':
      return { ...initialState };

    default:
      return state;
  }
};

export const useBilling = (shouldFetch = true) => {
  const [state, dispatch] = useReducer(billingReducer, initialState);

  const fetchBilling = useCallback(async (page = 1) => {
    dispatch({ type: 'FETCH_START' });

    try {
      const promise = fetchBillingHistory(page);
      const response = await apiResponseHandler<IBillingHistoryApiResponse>(promise);
      if (response.type === ApiResponseEnum.Success) {
        dispatch({ type: 'FETCH_SUCCESS', payload: { billing: response.data, page } });
      } else {
        dispatch({ type: 'FETCH_FAILURE', error: response.error });
      }
    } catch (error) {
      dispatch({ type: 'FETCH_FAILURE', error: 'Failed to fetch billing history' });
    }
  }, []);

  useEffect(() => {
    if (shouldFetch) {
      fetchBilling(state.currentPage);
    }
  }, [fetchBilling, shouldFetch]);

  return {
    ...state,
    fetchNextPage: () => {
      if (state.currentPage < state.totalPages) {
        fetchBilling(state.currentPage + 1);
      }
    },
    fetchSpecificPage: (page: number) => {
      if (page >= 1 && page <= state.totalPages) {
        fetchBilling(page);
      }
    },
    resetList: () => dispatch({ type: 'RESET_LIST' }),
  };
};
